// Internal app registry -- AD group ids, labels, and link targets.
//
// Pairs with `azure-function/src/shared/appCatalog.js`, which owns the
// capability descriptions fed to the routing classifier. The two files share
// only the app KEY strings; keep those in sync when adding an app.
//
// Why the split: capability copy is prompt content and changes often, so it
// lives backend-side where it can be edited without a frontend build or a slot
// swap. Group ids and styling are render concerns and belong here.
//
// This registry is the access filter for app routing. Everything that decides
// "may we mention this app to this user" flows through getAccessibleAppKeys().

import { authService } from '../services/authService';
import { APP_GROUPS } from './appGroups';

export type AppKey =
  | 'TRAINING'
  | 'BUMBLB'
  | 'SPACE_AVAILABLE'
  | 'RESCHAT'
  | 'INSIGHT_MANAGER'
  | 'LEASE_CHAT';

export interface AppRegistryEntry {
  key: AppKey;
  /** Display name. Must match `name` in the backend catalog. */
  label: string;
  /** Azure AD group object id gating this app. */
  groupId: string;
  url: string;
  /** One-liner used on the handoff card and the welcome chips. */
  blurb: string;
  /** Short prompt shown as a starter chip on the empty state. */
  starterPrompt: string;
}

export const APP_REGISTRY: Record<AppKey, AppRegistryEntry> = {
  TRAINING: {
    key: 'TRAINING',
    label: 'Training',
    groupId: APP_GROUPS.TRAINING,
    url: 'https://training.rrrealty.com/',
    blurb: 'Courses, completion status, and onboarding material.',
    starterPrompt: 'What training do I still need to finish?',
  },
  BUMBLB: {
    key: 'BUMBLB',
    label: 'bumbl.b',
    groupId: APP_GROUPS.BUMBLB,
    url: 'https://invoice.rrrealty.com/',
    blurb: 'Invoice status, coding, and approval routing.',
    starterPrompt: 'Where does an invoice get approved?',
  },
  SPACE_AVAILABLE: {
    key: 'SPACE_AVAILABLE',
    label: 'Space Available',
    groupId: APP_GROUPS.SPACE_AVAILABLE,
    url: 'https://space.rrrealty.com/',
    blurb: 'Available suites and space marketing across the portfolio.',
    starterPrompt: 'What space do we have available right now?',
  },
  RESCHAT: {
    key: 'RESCHAT',
    label: 'ResChat',
    groupId: APP_GROUPS.RESCHAT,
    url: 'https://reschat.rrrealty.com/',
    blurb: 'Residential and multifamily questions.',
    starterPrompt: 'Ask about our residential portfolio',
  },
  INSIGHT_MANAGER: {
    key: 'INSIGHT_MANAGER',
    label: 'InsightManager',
    groupId: APP_GROUPS.INSIGHT_MANAGER,
    url: 'https://insightmanager.rrrealty.com/',
    blurb: 'Rent roll, occupancy, NOI, lease expirations, AR aging, and debt.',
    starterPrompt: 'What can InsightManager tell me?',
  },
  LEASE_CHAT: {
    key: 'LEASE_CHAT',
    label: 'LeaseChat',
    groupId: APP_GROUPS.LEASE_CHAT,
    url: 'https://lease.rrrealty.com/',
    blurb: 'What a specific lease says -- clauses, provisions, abstracts.',
    starterPrompt: 'Ask what a lease says about a clause',
  },
};

export const ALL_APP_KEYS = Object.keys(APP_REGISTRY) as AppKey[];

/**
 * Apps the signed-in user can actually open, per their AD groups claim.
 *
 * FAILS CLOSED. Returns [] when group membership cannot be determined --
 * signed out, or the groups claim was replaced by an overage reference (see
 * authService.hasResolvedGroups). An empty result means the assistant
 * mentions no apps at all, which is the correct degradation: silence beats
 * pointing someone at a door that will not open for them.
 */
export function getAccessibleAppKeys(): AppKey[] {
  if (!authService.hasResolvedGroups()) return [];
  return ALL_APP_KEYS.filter((key) => authService.hasGroupAccess(APP_REGISTRY[key].groupId));
}

export function getAccessibleApps(): AppRegistryEntry[] {
  return getAccessibleAppKeys().map((key) => APP_REGISTRY[key]);
}

/**
 * Render-time guard. Called again on the response path before a handoff card
 * is shown, so a suggestion is re-checked against the CURRENT token rather
 * than trusted because the backend echoed it back.
 */
export function canAccessApp(key: string | null | undefined): key is AppKey {
  if (!key) return false;
  // hasOwnProperty, not a bare index: 'constructor' and 'toString' resolve
  // through the prototype chain to truthy values that would otherwise reach
  // the group check with an undefined groupId.
  if (!Object.prototype.hasOwnProperty.call(APP_REGISTRY, key)) return false;
  if (!authService.hasResolvedGroups()) return false;
  return authService.hasGroupAccess(APP_REGISTRY[key as AppKey].groupId);
}

/** Registry entry for a key, but only if the user may access it. Else null. */
export function getAccessibleApp(key: string | null | undefined): AppRegistryEntry | null {
  return canAccessApp(key) ? APP_REGISTRY[key] : null;
}
